
import { useEffect, useMemo, useState } from 'react';
import { Check, Eye, EyeOff, KeyRound, Save, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import {
  PainelPermissionsDialogShell,
  PainelRoleIcon,
  BUILTIN_ROLE_VISUALS,
} from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  workspaceModalSelectContentClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { painelInputClass, painelSelectTriggerClass } from '@/components/painel/cms/painel-form';
import {
  PAINEL_PERMISSION_LABELS,
  PAINEL_ROLE_DESCRIPTIONS,
  PAINEL_ROLE_LABELS,
  ROLE_PERMISSIONS,
  isAbsoluteAdminUser,
  type PainelMockUser,
  type PainelRole,
  type PainelUserStatus,
} from '@/lib/painel-rbac';
import { PainelXatRankField } from '@/components/painel/PainelXatRankField';
import { PainelHelpCenterRoleField } from '@/components/painel/PainelHelpCenterRoleField';
import {
  resolvePainelUserXatRankOptionId,
  xatRankOptionIdToOfficialLabel,
} from '@/lib/painel-user-xat-rank';
import {
  helpCenterRoleOptionToValue,
  resolvePainelUserHelpCenterRoleOption,
} from '@/lib/painel-user-help-center-role';
import type { StaffXatRankOptionId } from '@/lib/equipe-xat-ranks';
import type { SiteStaffRole } from '@/lib/cms-types';
import type { PainelUserEditableFields } from '@/lib/painel-users-store';
import { cn } from '@/lib/utils';

const ROLE_ORDER: PainelRole[] = ['super_admin', 'admin', 'editor', 'reviewer'];

const MODAL_SELECT_CONTENT_CLASS = workspaceModalSelectContentClass;

function isSelectPortalTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest('[data-radix-select-content]') ||
      target.closest('[data-radix-popper-content-wrapper]') ||
      target.closest('[role="listbox"]')
  );
}

type PainelUserEditorModalProps = {
  user: PainelMockUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, patch: PainelUserEditableFields) => void | Promise<void>;
  canChangePassword?: boolean;
};

export function PainelUserEditorModal({
  user,
  open,
  onOpenChange,
  onSave,
  canChangePassword = false,
}: PainelUserEditorModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PainelRole>('editor');
  const [status, setStatus] = useState<PainelUserStatus>('active');
  const [xatRankOptionId, setXatRankOptionId] = useState<StaffXatRankOptionId | 'none'>('none');
  const [helpCenterRoleOptionId, setHelpCenterRoleOptionId] = useState<SiteStaffRole | 'none'>('none');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (user && open) {
      setName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setStatus(user.status);
      setXatRankOptionId(resolvePainelUserXatRankOptionId(user));
      setHelpCenterRoleOptionId(resolvePainelUserHelpCenterRoleOption(user));
      setNewPassword('');
      setConfirmPassword('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setSaveError(null);
      setPasswordError(null);
    }
  }, [user, open]);

  const rolePermissions = ROLE_PERMISSIONS[role] ?? [];
  const visual = BUILTIN_ROLE_VISUALS[role];
  const isAbsoluteAdmin = isAbsoluteAdminUser(user);

  const pendingPatch = useMemo<PainelUserEditableFields | null>(() => {
    if (!user || !name.trim()) return null;
    const patch: PainelUserEditableFields = {
      name: name.trim(),
      email: email.trim(),
      role,
      status,
      officialXatRole: xatRankOptionIdToOfficialLabel(xatRankOptionId),
      helpCenterRole: helpCenterRoleOptionToValue(helpCenterRoleOptionId),
    };
    if (canChangePassword && newPassword.trim()) {
      patch.password = newPassword.trim();
    }
    return patch;
  }, [user, name, email, role, status, xatRankOptionId, helpCenterRoleOptionId, canChangePassword, newPassword]);

  const confirmDescription = useMemo(() => {
    if (!user || !pendingPatch) return 'Confirmar alterações neste usuário?';

    const lines: string[] = [];
    if (pendingPatch.name !== user.name) {
      lines.push(`Nome: ${user.name} → ${pendingPatch.name}`);
    }
    if (pendingPatch.email !== user.email) {
      lines.push(`E-mail: ${user.email} → ${pendingPatch.email}`);
    }
    if (pendingPatch.role !== user.role) {
      lines.push(
        `Cargo: ${PAINEL_ROLE_LABELS[user.role]} → ${PAINEL_ROLE_LABELS[pendingPatch.role]}`
      );
    }
    if (pendingPatch.status !== user.status) {
      lines.push(`Status: ${user.status === 'active' ? 'Ativo' : 'Inativo'} → ${pendingPatch.status === 'active' ? 'Ativo' : 'Inativo'}`);
    }

    const currentXatRank = resolvePainelUserXatRankOptionId(user);
    if (xatRankOptionId !== currentXatRank) {
      lines.push('Cargo no xat será atualizado.');
    }
    if (canChangePassword && newPassword.trim()) {
      lines.push('A senha do usuário será alterada.');
    }

    return lines.length > 0 ? (
      <span className="block space-y-1.5">
        {lines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </span>
    ) : (
      'Nenhum campo foi alterado. Deseja salvar mesmo assim?'
    );
  }, [user, pendingPatch, xatRankOptionId, canChangePassword, newPassword]);

  const validatePasswordFields = (): boolean => {
    if (!canChangePassword || !newPassword.trim()) {
      setPasswordError(null);
      return true;
    }

    if (newPassword.trim().length < 4) {
      setPasswordError('A nova senha deve ter pelo menos 4 caracteres.');
      return false;
    }

    if (newPassword.trim() !== confirmPassword.trim()) {
      setPasswordError('A confirmação da senha não coincide.');
      return false;
    }

    setPasswordError(null);
    return true;
  };

  const handleRequestSave = () => {
    if (!pendingPatch || !validatePasswordFields()) return;
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!user || !pendingPatch) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(user.id, pendingPatch);
      setConfirmOpen(false);
      onOpenChange(false);
    } catch {
      setSaveError('Não foi possível salvar as alterações. Verifique os dados e tente novamente.');
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
        onPointerDownOutside={(event) => {
          if (isSelectPortalTarget(event.target)) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isSelectPortalTarget(event.target)) event.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">
          Editar usuário{user ? `: ${user.name}` : ''}
        </DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <PainelRoleIcon config={visual} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Editar usuário
                </p>
                <h2 className="mt-1 truncate font-poppins text-xl font-bold text-white sm:text-2xl">
                  {user?.name ?? 'Usuário'}
                </h2>
                <p className="mt-1.5 font-inter text-sm text-slate-400">
                  {isAbsoluteAdmin
                    ? 'Conta admin absoluta — cargo e status não podem ser alterados.'
                    : 'Altere dados, cargo e estado do usuário no painel.'}
                </p>
              </div>
            </div>
          </div>

          <PainelRocketScroll
            className='max-h-[min(56vh,540px)] bg-[#030023]/30'
            viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="user-name" className="text-slate-300">
                  Nome
                </Label>
                <input
                  id="user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={painelInputClass}
                  placeholder="Ex: Guinho"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-email" className="text-slate-300">
                  E-mail
                </Label>
                <input
                  id="user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={painelInputClass}
                  placeholder="usuario@ajuda"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Cargo</Label>
                <Select
                  value={role}
                  onValueChange={(v) => setRole(v as PainelRole)}
                  disabled={isAbsoluteAdmin}
                >
                  <SelectTrigger className={painelSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className={MODAL_SELECT_CONTENT_CLASS}>
                    {ROLE_ORDER.map((r) => (
                      <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">
                        {PAINEL_ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as PainelUserStatus)}
                  disabled={isAbsoluteAdmin}
                >
                  <SelectTrigger className={painelSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className={MODAL_SELECT_CONTENT_CLASS}>
                    <SelectItem value="active" className="focus:bg-white/10 focus:text-white">
                      Ativo
                    </SelectItem>
                    <SelectItem value="inactive" className="focus:bg-white/10 focus:text-white">
                      Inativo
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <PainelXatRankField
                  value={xatRankOptionId}
                  onChange={setXatRankOptionId}
                  selectContentClassName={MODAL_SELECT_CONTENT_CLASS}
                />
              </div>
              <div className="sm:col-span-2">
                <PainelHelpCenterRoleField
                  value={helpCenterRoleOptionId}
                  onChange={setHelpCenterRoleOptionId}
                  selectContentClassName={MODAL_SELECT_CONTENT_CLASS}
                />
              </div>
            </div>

            {canChangePassword ? (
              <div className="mt-6 rounded-2xl border border-white/10 bg-blue-950/30 p-4 sm:p-5">
                <div className="mb-4 flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-sky-400/70" aria-hidden />
                  <h3 className="font-poppins text-xs font-bold uppercase tracking-[0.14em] text-slate-300">
                    Alterar senha
                  </h3>
                </div>
                <p className="mb-4 font-inter text-xs leading-relaxed text-slate-400">
                  Deixe em branco para manter a senha atual. Apenas Super Admin pode redefinir senhas.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="user-new-password" className="text-slate-300">
                      Nova senha
                    </Label>
                    <div className="relative">
                      <input
                        id="user-new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setPasswordError(null);
                        }}
                        className={cn(painelInputClass, 'pr-11')}
                        placeholder="Mínimo 4 caracteres"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-200"
                        aria-label={showNewPassword ? 'Esconder senha' : 'Mostrar senha'}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-confirm-password" className="text-slate-300">
                      Confirmar senha
                    </Label>
                    <div className="relative">
                      <input
                        id="user-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError(null);
                        }}
                        className={cn(painelInputClass, 'pr-11')}
                        placeholder="Repita a nova senha"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-200"
                        aria-label={showConfirmPassword ? 'Esconder senha' : 'Mostrar senha'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                {passwordError ? (
                  <p className="mt-3 font-inter text-xs text-rose-300">{passwordError}</p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6 rounded-2xl border border-white/10 bg-blue-950/30 p-4 sm:p-5">
              <div className="mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-400/70" aria-hidden />
                <h3 className="font-poppins text-xs font-bold uppercase tracking-[0.14em] text-slate-300">
                  Permissões do cargo {PAINEL_ROLE_LABELS[role]}
                </h3>
                <span className="ml-auto font-inter text-[11px] text-slate-500">
                  {rolePermissions.length} ativas
                </span>
              </div>
              <p className="mb-3 font-inter text-xs leading-relaxed text-slate-400">
                {PAINEL_ROLE_DESCRIPTIONS[role]}
              </p>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {rolePermissions.map((permission) => (
                  <li
                    key={permission}
                    className="flex items-center gap-2 font-inter text-[13px] text-blue-50"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-sky-400/50 bg-sky-500/25 text-sky-100">
                      <Check className="h-3 w-3" aria-hidden />
                    </span>
                    {PAINEL_PERMISSION_LABELS[permission]}
                  </li>
                ))}
              </ul>
            </div>
          </PainelRocketScroll>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-8">
            {saveError ? (
              <p className="font-inter text-xs text-rose-300 sm:mr-auto">{saveError}</p>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              className="h-10 rounded-xl text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!name.trim() || saving}
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              onClick={handleRequestSave}
            >
              <Save className="mr-2 h-4 w-4" />
              Salvar alterações
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>

    <PainelConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Confirmar alterações?"
      description={confirmDescription}
      confirmLabel={saving ? 'A guardar…' : 'Salvar alterações'}
      tone="info"
      onConfirm={() => {
        void handleConfirmSave();
      }}
    />
    </>
  );
}
